import React, {useState, useEffect} from 'react';
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { isRegister, userToken, login, logout } from "../../features/user";
import axios from "axios";
import { API_OK, API_URL, DEFAULT_ERROR_TEXT, API_EXCEPTION } from '../../Constants';
import { useTranslation } from 'react-i18next';
import {apiErrorMessage, checkApiSessionErrorMessage, getLang, isStrongPassword} from "../../Helper"

import Frame from '../widgets/Frame';
import FormErrors from '../widgets/FormErrors';
import { useSubmitGuard } from "../../hooks/useSubmitGuard";
import { useCaptcha } from "../../captcha";
import { isFeatureOn } from "../../portalSettings";
import { useEnterSubmit } from '../../hooks/useEnterSubmit';

/**
 * הצעד האחרון בהרשמה - **ובשתי צורות.**
 *
 * ## הדילוג
 *
 * `skipCredentials` מסיים את ההרשמה בלי שם משתמש ובלי סיסמה. הלקוח ייכנס מכאן והלאה
 * בהתחברות מהירה: טלפון או מייל, וקוד.
 *
 * **וזה עובד רק בגלל מה שההרשמה כבר עשתה.** היא אימתה את שני הערוצים בקודים לפני
 * שהחשבון נוצר, כלומר בידה שני ערכים **מוכחים** - וזה בדיוק מה שההתחברות המהירה
 * מחפשת. אין כאן ויתור על הוכחה; יש כאן ויתור על סוד שני שלקוחות שוכחים, ושבגללו
 * קיימים שני מסכי שחזור.
 *
 * ## למה הדילוג הוא הכפתור הראשי, והסיסמה מתחתיו
 *
 * זה מה שאנחנו רוצים שרוב הנרשמים יעשו, והמסך צריך לומר את זה. שדה שם המשתמש הוא
 * המקום שבו נשברת ההרשמה - ארבע דרישות סיסמה, ושם משתמש שצריך להמציא ולזכור - ומי
 * שממשיך משם מגיע לשני מסכי שחזור בפעם הבאה.
 *
 * **והבחירה נשארת.** מי שרוצה סיסמה פותח את הטופס ומקבל בדיוק את מה שהיה. הדילוג
 * אינו מוצג כלל כשההתחברות המהירה כבויה אצל הריסיילר - וזה גם השער בשרת: חשבון בלי
 * סיסמה שאין לו דרך אחרת להיכנס הוא חשבון נעול.
 */

export function CreateCredentails() {
    const [state, setState] = useState({username: "", password: "", confirmPassword: "", errors: {}, showPasswordForm: false });


    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    // One save per click, so a slow answer cannot become two accounts.
    const submit = useSubmitGuard();
    /**
     * הקאפצ'ה. פעילה רק כשההגדרה הגיעה מהשרת, ומה שנשלח נבדק שם בלי קשר למסך.
     */
    const captcha = useCaptcha('createCredentials');
    const isUserRegister = useSelector(isRegister);
    const reseller = useSelector((state) => state.reseller.value);

    /**
     * הדילוג מוצע רק כשההתחברות המהירה פתוחה אצל הריסיילר.
     *
     * **המסך אינו השער** - `createCredentials` בודק את אותה הגדרה בשרת - אבל להציע
     * פעולה שתידחה זה לשלוח את הלקוח לדלת שתיטרק בפניו, בשלב האחרון של ההרשמה.
     */
    const canSkip = isFeatureOn(reseller, 'allowQuickLogin');

    useEffect(()=>{
        if(!isUserRegister){
            navigate("/register");
        }
        // eslint-disable-next-line
    },[]);

    const handleSubmit = async (e, skipCredentials = false) => {
        e.preventDefault();
        /**
         * הדילוג אינו עובר את בדיקת הטופס, ובמכוון: אין בו שם משתמש ואין בו סיסמה,
         * ו-`validateForm` היה דוחה אותו על שני שדות שהוא בא לוותר עליהם.
         */
        if(skipCredentials || !validateForm()) {
            let errorMessage = DEFAULT_ERROR_TEXT;

            let config = {'headers': {"token": userToken(), "lang": getLang()}};

            const data = skipCredentials
                ? {skipCredentials: true}
                : {userName: state.username.trim(), password: state.password.trim()};

            // הטוקן מצורף כאן, כדי שהפעולה שהוא נוצר עבורה תיקבע במקום אחד.
            const payload = await captcha.attach(data);
            // אין טוקן - אין מה לשלוח. ההסבר כבר מצויר על המסך.
            if (!payload) {
                return;
            }
        
            try {
                const reply = await submit.run(() => axios.post(API_URL + 'createCredentials', payload, config));
                // A blocked second click returns nothing; the first one is still running.
                if (!reply) {
                    return;
                }
                if(reply.hasOwnProperty('data') && reply.data.hasOwnProperty('responseStatus') && reply.data.responseStatus === API_OK
                        && reply.data.hasOwnProperty('token')){
                    dispatch(login({token : reply.data.token}));
                    /**
                     * **חשבון ללא סיסמה אינו נשלח לרשום שיטת אימות מיד.**
                     *
                     * מסך הרישום הזה קיים כדי להוסיף גורם שני מעל הסיסמה, וכאן אין
                     * סיסמה: הכניסה עצמה היא קוד חד-פעמי לערוץ מוכח, כלומר גורם
                     * ההחזקה נדרש בכל כניסה ממילא. לשלוח לשם היה לבקש מהלקוח לרשום
                     * שיטה שנייה לאותו טלפון שהוא בדיוק נכנס איתו.
                     *
                     * מה שכן מוצע לו הוא מפתח גישה, במסך הסיום - וזה גם מה שמוציא
                     * את ההודעות מהמשוואה בכניסות הבאות.
                     */
                    navigate(reply.data.isPasswordless === true ? "/congratulations" : "/two-step-verification");
                }else{
                    if((reply.data.responseStatus === API_EXCEPTION) && (reply.data.hasOwnProperty("message"))){
                        errorMessage = apiErrorMessage(reply.data, t);
                    }
                    if(!checkApiSessionErrorMessage(errorMessage, dispatch, logout, navigate)) {
                        setState({
                            ...state,
                            errors: {server_error: errorMessage}
                        });
                    }
                };
            } catch (e) {
                setState({
                    ...state,
                    errors: {server_error: errorMessage}
                });
            }
        }
    };

    const handleChange = (e) => {
        delete state.errors[e.target.id];
        setState({
            ...state,
            [e.target.id]: e.target.value,
            errors: {...state.errors},
        });
    };

    const validateForm = () => {
        const errors = {};
        if (!state.username.trim() /*|| state.firstName.trim().length < 4*/) {
            errors['username'] = t('Username is required');
        }

        if (!state.password) {
             errors['password'] = t('Password is required');
        } else if (!isStrongPassword(state.password)) {
             errors['password'] = t('The password you selected does not meet our security requirements');
        }

        if (state.confirmPassword !== state.password) {
             errors['confirmPassword'] = t('Passwords do not match');
        }

        setState({
            ...state,
            errors: errors,
        });

        return Object.keys(errors).length > 0;
    };

    // const hasErrors = () => {
    //     return Object.keys(state.errors).length > 0
    // }

    const toggleShowPassword = (e) => {
        if(e.target.parentElement.querySelector('input')) {
            e.target.parentElement.querySelector('input').type = e.target.parentElement.querySelector('input').type === "password"?"text":"password";
        }
    };


    /**
     * Enter ממשיך לשלב הבא, ולכל אחד משני המסכים הפעולה שלו.
     *
     * **לפני היציאה המוקדמת שמתחת**, ולא אחריה: הוק שנקרא רק באחד משני המסלולים
     * משנה את סדר ההוקים בין רינדורים, וזה שגיאה ולא סגנון.
     *
     * `skipCredentials` נגזר מאותו תנאי שמכריע איזה מסך מצויר, כדי ש-Enter יעשה
     * בדיוק את מה שהכפתור שעל המסך עושה: במסך הדילוג הוא מדלג, ובטופס הסיסמה הוא
     * שומר אותה. ראה `useEnterSubmit`.
     */
    useEnterSubmit(
        (e) => handleSubmit(e, canSkip && !state.showPasswordForm),
        !submit.busy
    );

    /**
     * מסך הדילוג - **הכפתור הראשי, והסיסמה כקישור מתחתיו.**
     *
     * זה מה שאנחנו רוצים שרוב הנרשמים יעשו, והמסך אומר את זה בסדר שלו. מי שרוצה
     * סיסמה פותח את הטופס ומקבל בדיוק את מה שהיה - שום מסלול לא נסגר.
     */
    if (canSkip && !state.showPasswordForm) {
        return (
            <Frame>
                <div className='main main-create-user'>
                    <div className='main-top'>
                        <h1>{t("Almost done")}</h1>
                    </div>
                    <div className='main-body'>
                        <div className='right-main'>
                            <div className='right-main-inner'>
                                <h2>{t("You can enter with your phone or email and a one-time code, with no password to remember")}</h2>
                                {/* ההסבר הוא חלק מהמנגנון ולא קישוט: לקוח שאינו יודע
                                    איך ייכנס בפעם הבאה יבחר סיסמה מתוך חוסר ודאות. */}
                                <h2 className='list-item'>{t("We already confirmed your phone and your email in this registration")}</h2>
                                <h2 className='list-item'>{t("Next time, enter one of them and we will send you a code")}</h2>
                                {/*
                                    **מה שנאמר כאן חייב להיות מה שקיים.**

                                    השורה הזו הבטיחה "תמיד אפשר לקבוע סיסמה בהמשך,
                                    מהגדרות החשבון", ואין מסך כזה: `changePassword`
                                    דורש סיסמה נוכחית ושם משתמש, ולחשבון ללא סיסמה
                                    אין לא זה ולא זה. הבטחה שאין מאחוריה מסלול היא
                                    הדבר שהלקוח יגלה בדיוק כשהוא יזדקק לה.

                                    מה שנאמר במקומה הוא מה שכן קיים, וזו גם התשובה
                                    האמיתית לחשש: אפשר לשמור מפתח גישה במכשיר, ואז
                                    הכניסה היא נגיעה אחת בלי שום הודעה.
                                */}
                                <h2 className='list-item'>{t("Later you can save a passkey on your device, and sign in with a single touch")}</h2>

                                <div {...submit.buttonProps({base: 'button button-space-between button-teal mt30'})} onClick={(e) => handleSubmit(e, true)}>
                                    <div className='mr20'>{submit.busy ? t("Saving...") : t("Finish without a password")}</div>
                                    {submit.busy ? <span className='button-spinner ml20' /> : <img src='/images/less-than-white.png' className='ml20 button-icon less-than-icon' alt=''/>}
                                </div>

                                <h2 className='mt20 teal pointer' onClick={() => setState({...state, showPasswordForm: true, errors: {}})}>
                                    {t("I prefer a username and a password")}
                                </h2>

                                <FormErrors errors={state.errors} />
                            </div>
                        </div>
                        <div className='left-main'>
                            <img src='/images/person-computer.png' className='content-logo' alt=''/>
                        </div>
                    </div>
                </div>
            </Frame>
        );
    }

    return (
            <Frame>
                <div className='main main-create-user'>
                    <div className='main-top'>
                        <h1>{t("Create a user")}</h1>
                    </div>
                    <div className='main-body'>
                        <div className='right-main'>
                            <form onSubmit={handleSubmit}> 
                                <h2>{t("Please create a username using English letters (minimum 6 characters)")}</h2>
                                <div className='input blue-border'><input className={state.errors.username?"error":""} required type="text" id="username" placeholder={t("Select a username")} onChange={(e) => handleChange(e)} /></div>
                                <h2 className='list-item'>{t("Between 8 and 40 characters")}</h2>
                                <h2 className='list-item'>{t("Capital and lowercase letters")}</h2>
                                <h2 className='list-item'>{t("At least one number")}</h2>
                                <h2 className='list-item'>{t("At least one special character")}</h2>

                                <div className='mt30 input blue-border'><input className={state.errors.password?"error":""} required type="password" id="password" placeholder={t("Choose a password")} onChange={(e) => handleChange(e)} /><img src='/images/show-password-icon.png' className='input-icon ml20' onClick={(e) => toggleShowPassword(e)} alt=''/></div>
                                <div className='input blue-border'><input className={state.errors.confirmPassword?"error":""} required type="password" id="confirmPassword" placeholder={t("Please repeat the password")} onChange={(e) => handleChange(e)} /><img src='/images/show-password-icon.png' className='input-icon ml20' onClick={(e) => toggleShowPassword(e)} alt=''/></div>
                                {captcha.node}
                                <div {...submit.buttonProps({base: 'button button-space-between button-teal'})} onClick={(e) => handleSubmit(e)}>  <div className='mr20'>{submit.busy ? t("Saving...") : t("next")}</div> {submit.busy ? <span className='button-spinner ml20' /> : <img src='/images/less-than-white.png' className='ml20 button-icon less-than-icon' alt=''/>} </div>

                                {/* הדרך חזרה לדילוג - מי שפתח את הטופס ושינה את דעתו
                                    אינו אמור לחפש את כפתור החזרה של הדפדפן. */}
                                {canSkip&&<h2 className='mt20 teal pointer' onClick={() => setState({...state, showPasswordForm: false, errors: {}})}>{t("Back to entering without a password")}</h2>}

                                <FormErrors errors={state.errors} />
                            </form>
                        </div>
                        <div className='left-main'>
                            <img src='/images/person-computer.png' className='content-logo' alt=''/>
                        </div>
                    </div>
                </div>
            </Frame>
    );
}